import React from "react";
import { ShieldCheck } from "lucide-react";

/**
 * Owner Trust badge — payment reliability + engagement, computed server-side.
 *
 * Data comes from GET /api/listings and GET /api/listings/{id} which now embed
 * `owner_trust: { score: 0-100, band: { label, color } }` on every row where
 * the owner is a real Find2Near account (external/partner/public rows are omitted).
 *
 * Bands (mirrors services/trust_score.py `_bucket`):
 *   80+ Excellent · emerald
 *   60+ Good      · green
 *   40+ Fair      · amber
 *   20+ Building  · orange
 *    0+ New       · stone
 */
const COLOR_TO_CLASSES = {
  emerald: "bg-emerald-50 border-emerald-300 text-emerald-800",
  green:   "bg-green-50 border-green-300 text-green-800",
  amber:   "bg-amber-50 border-amber-300 text-amber-800",
  orange:  "bg-orange-50 border-orange-300 text-orange-800",
  stone:   "bg-stone-100 border-stone-300 text-stone-700",
};

const DOT_TO_CLASSES = {
  emerald: "bg-emerald-500",
  green:   "bg-green-500",
  amber:   "bg-amber-500",
  orange:  "bg-orange-500",
  stone:   "bg-stone-400",
};

export default function TrustBadge({ trust, size = "sm", showLabel = true, className = "" }) {
  if (!trust || typeof trust.score !== "number") return null;
  const color = trust.band?.color || "stone";
  const label = trust.band?.label || "New";
  const outer = COLOR_TO_CLASSES[color] || COLOR_TO_CLASSES.stone;
  const dot = DOT_TO_CLASSES[color] || DOT_TO_CLASSES.stone;
  const sizeCls =
    size === "xs"
      ? "text-[10px] px-1.5 py-0.5 gap-1"
      : size === "md"
      ? "text-sm px-2.5 py-1 gap-1.5"
      : "text-[11px] px-2 py-0.5 gap-1";
  const dotSize = size === "md" ? "w-2 h-2" : "w-1.5 h-1.5";
  return (
    <span
      title={`Trust score: ${trust.score}/100 — ${label} (payment reliability + engagement)`}
      className={`inline-flex items-center rounded-full border font-bold uppercase tracking-wider ${outer} ${sizeCls} ${className}`}
      data-testid="trust-badge"
      data-trust-score={trust.score}
      data-trust-band={label}
    >
      <span className={`rounded-full ${dot} ${dotSize}`} />
      {showLabel && (
        <>
          <ShieldCheck size={size === "xs" ? 10 : 11} strokeWidth={2.5} />
          <span>{label}</span>
          <span className="opacity-70">· {trust.score}</span>
        </>
      )}
    </span>
  );
}
